import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import useGet from "../read/useGet";
import usePut from "../put/usePut";
import useUpload from "../useUpload";

export const usePettyCashForm = ({
  companyEmployeeId,
  editTransaction,
  onSuccess,
  onClose,
  reset,
}) => {
  const [mediaId, setMediaId] = useState(editTransaction?.media_id || null);
  const [mediaPath, setMediaPath] = useState(editTransaction?.media_path || null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (editTransaction) {
      setMediaId(editTransaction.media_id || null);
      setMediaPath(editTransaction.media_path || null);
    } else {
      setMediaId(null);
      setMediaPath(null);
    }
  }, [editTransaction]);

  // 1. Fetch expense types listing
  const { list: expenseTypes } = useGet({
    url: "petty-cash/get_expenses_type_listing",
    initialData: {},
  });

  // 2. Fetch currencies listing
  const { list: currencyList } = useGet({
    url: "petty-cash/currency_list",
    initialData: { limit: 50 },
  });

  // 3. Fetch entities listing
  const { list: entityList } = useGet({
    url: "petty-cash/entity_list",
    initialData: { limit: 50 },
  });

  // 4. Fetch fund requests listing
  const { list: fundRequestList } = useGet({
    url: "petty-cash/fund_request_list",
    initialData: { limit: 50 },
  });

  // 5. Receipt upload mutation
  const uploadReceipt = useUpload({
    url: "/uploadImage",
    paramName: "thumbnail[]",
    onSuccess: (res) => {
      setIsUploading(false);
      if (res?.data?.data?.[0]) {
        const { image_id, image_url } = res.data.data[0];
        setMediaId(image_id);
        setMediaPath(image_url);
        toast.success("Receipt uploaded successfully");
      } else {
        toast.error("Failed to process uploaded receipt");
      }
    },
    onError: (err) => {
      setIsUploading(false);
      toast.error("Failed to upload receipt");
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    uploadReceipt.mutate({ file });
  };

  const removeReceipt = (fileInputRef) => {
    setMediaId(null);
    setMediaPath(null);
    if (fileInputRef?.current) {
      fileInputRef.current.value = "";
    }
  };

  // 6. Submit transaction mutation
  const { handleAuth: submitTransactionMutation } = usePut({
    url: "petty-cash/create_transaction",
    refreshUrl: [
      "petty-cash/transaction_listing",
      "petty-cash/statistics",
      "petty-cash/fetch_single_transaction",
    ],
    onSuccess: (res) => {
      toast.success(
        res?.data?.message ||
          (editTransaction ? "Transaction updated successfully" : "Transaction added successfully")
      );
      onSuccess?.();
      onClose();
      reset();
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message ||
          (editTransaction ? "Failed to update transaction" : "Failed to add transaction")
      );
    },
  });

  const { mutate: submitTransaction, isLoading: isSaving } = submitTransactionMutation;

  const handleSave = (data) => {
    const listOptions = expenseTypes.data?.data || [];
    const selectedId = data.expenses_type_id;

    const matchedOption = listOptions.find((opt) => String(opt.id) === String(selectedId));
    const finalId = matchedOption ? matchedOption.id : "";
    const finalName = matchedOption ? matchedOption.name : selectedId || "";

    const currencies = currencyList.data?.data || [];
    const selectedCurrency = currencies.find(
      (c) => String(c.currency_type_id) === String(data.currency_type_id)
    );

    const formData = {
      ...(editTransaction?.transaction_id && {
        transaction_id: editTransaction.transaction_id,
      }),
      expenses_date: data.expenses_date
        ? data.expenses_date instanceof Date
          ? data.expenses_date.toISOString().split("T")[0]
          : data.expenses_date
        : "",
      particulars: data.particulars,
      expenses_type_id: finalId,
      expenses_type_name: finalName,
      amount: data.amount,
      transaction_type: data.transaction_type,
      currency_type_id: data.currency_type_id || "",
      currency_symbol: selectedCurrency?.currency_symbol || "",
      entity_id: data.entity_id || "",
      fund_request_id: data.fund_request_id || "",
      company_employee_id: companyEmployeeId || "",
      invoice_no: data.invoice_no || "",
      chq_no: data.chq_no || "",
      chq_date: data.chq_date
        ? data.chq_date instanceof Date
          ? data.chq_date.toISOString().split("T")[0]
          : data.chq_date
        : "",
      additional_remarks: data.additional_remarks || "",
      media_id: mediaId || "",
    };
    submitTransaction(formData);
  };

  return {
    expenseTypes,
    currencyList,
    entityList,
    fundRequestList,
    mediaId,
    mediaPath,
    isUploading,
    isSaving,
    handleFileChange,
    removeReceipt,
    handleSave,
  };
};
